import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FiSend, FiThumbsUp, FiTrendingUp, FiClock } from 'react-icons/fi';
import { BiQuestionMark } from 'react-icons/bi';
import { useVirtual } from 'react-virtual';
import { useTheme } from 'styled-components';
import { GooSpinner } from 'react-spinners-kit';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  useBroadcastQuery,
  usePublicQuestionsQuery,
  usePublicQuestionMutation,
  usePublicQuestionVoteMutation,
  usePublicQuestionsChannel,
} from '@hooks';
import { Nullable } from '@common/types';
import { PublicQuestion } from '@common/types/api/public-questions/response';

import * as S from './styles';
import * as I from '../styles';
import { BaseInteractionProps } from '../../types';

type FormInputs = {
  question: string;
};

type SortType = 'votes' | 'recent';

// Helper to get initials from name
const getInitials = (name: string) => {
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Helper to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type QuestionCardProps = {
  question: PublicQuestion;
  onVote: (id: number) => void;
};

const QuestionCard = memo(({ question, onVote }: QuestionCardProps) => {
  const isHighlighted = question.user_has_voted || question.votes_count >= 2;

  return (
    <S.QuestionCard $highlighted={isHighlighted}>
      <S.AvatarWrapper>{getInitials(question.user.data.name)}</S.AvatarWrapper>

      <S.QuestionContent>
        <S.QuestionHeader>
          <S.UserName>{question.user.data.name}</S.UserName>
          <S.TimeStamp>{formatDate(question.created_at)}</S.TimeStamp>
        </S.QuestionHeader>

        <S.QuestionText>{question.question}</S.QuestionText>
      </S.QuestionContent>

      <S.VoteWrapper>
        <S.VoteButton
          type="button"
          onClick={() => onVote(question.id)}
          $voted={question.user_has_voted}
        >
          <FiThumbsUp />
        </S.VoteButton>
        <S.VoteCount $voted={question.user_has_voted}>
          {question.votes_count}
        </S.VoteCount>
      </S.VoteWrapper>
    </S.QuestionCard>
  );
});

QuestionCard.displayName = 'QuestionCard';

const PublicQuestions = ({ active: isActive }: BaseInteractionProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [sortType, setSortType] = useState<SortType>('votes');

  const formMethods = useForm<FormInputs>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(
      Yup.object().shape({
        question: Yup.string()
          .required('valid.asking.req')
          .max(255, 'A pergunta deve ter no máximo 255 caracteres'),
      }),
    ),
  });
  const { handleSubmit, reset } = formMethods;

  const { data: room } = useBroadcastQuery({
    select: ({ id, settings }) => ({
      id,
      isPublicQuestionsEnabled: !!settings.data.public_questions,
    }),
    notifyOnChangeProps: ['data'],
  });

  const questionsQuery = usePublicQuestionsQuery(room?.id);

  const { mutateAsync: createQuestion, isLoading: isCreating } =
    usePublicQuestionMutation(room?.id);

  const { mutateAsync: voteQuestion } = usePublicQuestionVoteMutation(room?.id);

  const parentRef = useRef<Nullable<HTMLElement>>(null);

  // Sort questions based on sortType
  const sortedQuestions = useMemo(() => {
    if (!questionsQuery.data) return [];

    const questions = [...questionsQuery.data];

    if (sortType === 'votes') {
      return questions.sort((a, b) => b.votes_count - a.votes_count);
    }
    // Recent - sort by created_at descending
    return questions.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [questionsQuery.data, sortType]);

  const rowVirtualizer = useVirtual({
    size: sortedQuestions.length,
    parentRef,
    estimateSize: useCallback(() => 140, []),
    keyExtractor: useCallback(
      (index: number) => sortedQuestions[index]?.id || index,
      [sortedQuestions],
    ),
    overscan: 5,
  });
  const { virtualItems, scrollToOffset, totalSize } = rowVirtualizer;

  // Pusher real-time
  usePublicQuestionsChannel(room?.id);

  const handleQuestionSubmit = useCallback(
    async ({ question }: FormInputs) => {
      reset();
      await createQuestion(question);

      // Scroll para o topo após criar pergunta
      setTimeout(() => {
        scrollToOffset(0, {
          align: 'start',
        });
      }, 500);
    },
    [createQuestion, reset, scrollToOffset],
  );

  const handleVote = useCallback(
    async (questionId: number) => {
      await voteQuestion(questionId);
    },
    [voteQuestion],
  );

  const questionsCount = questionsQuery.data?.length || 0;

  if (!room?.isPublicQuestionsEnabled) {
    return (
      <S.Container $hide $disabled={!isActive}>
        <S.MessageContainer>
          <BiQuestionMark size="3rem" />
          <p>{t('pages.broadcast.publicQuestions.disabled')}</p>
        </S.MessageContainer>
      </S.Container>
    );
  }

  if (questionsQuery.isLoading) {
    return (
      <S.Container $hide={false} $disabled={!isActive}>
        <S.MessageContainer>
          <GooSpinner size={50} color={theme.colors.brand.primary.default} />
          <p>{t('pages.broadcast.publicQuestions.loading')}</p>
        </S.MessageContainer>
      </S.Container>
    );
  }

  const hasNoQuestions = sortedQuestions.length === 0;

  return (
    <S.Container
      autoComplete="off"
      onSubmit={handleSubmit(handleQuestionSubmit)}
      $hide={!room?.isPublicQuestionsEnabled}
      $disabled={!isActive}
    >
      <FormProvider {...formMethods}>
        <S.FilterTabs>
          <S.FilterTab
            type="button"
            $active={sortType === 'votes'}
            onClick={() => setSortType('votes')}
          >
            <FiTrendingUp />
            {t('pages.broadcast.publicQuestions.mostVoted')}
          </S.FilterTab>
          <S.FilterTab
            type="button"
            $active={sortType === 'recent'}
            onClick={() => setSortType('recent')}
          >
            <FiClock />
            {t('pages.broadcast.publicQuestions.recent')}
          </S.FilterTab>
          <S.HeaderBadge>
            {questionsCount} {t('pages.broadcast.tabs.askings')}
          </S.HeaderBadge>
        </S.FilterTabs>

        <I.InteractionWrapper>
          <I.ContentWrapper
            containerRef={(ref) => {
              parentRef.current = ref;
            }}
          >
            {hasNoQuestions ? (
              <S.MessageContainer>
                <BiQuestionMark size="3rem" />
                <p>{t('pages.broadcast.publicQuestions.empty')}</p>
              </S.MessageContainer>
            ) : (
              <I.VirtualizedList $height={totalSize}>
                {virtualItems.map((virtualRow) => {
                  const question = sortedQuestions[virtualRow.index];

                  return (
                    <I.VirtualizedItemWrapper
                      key={question.id}
                      ref={virtualRow.measureRef}
                      $translateY={virtualRow.start}
                    >
                      <QuestionCard question={question} onVote={handleVote} />
                    </I.VirtualizedItemWrapper>
                  );
                })}
              </I.VirtualizedList>
            )}
          </I.ContentWrapper>

          <S.QuestionInput
            required
            name="question"
            type="text"
            placeholder={t('pages.broadcast.asking.title')}
            maxLength={255}
            disabled={isCreating}
            buttonIcon={
              isCreating ? (
                <GooSpinner
                  size={20}
                  color={theme.colors.brand.primary.default}
                />
              ) : (
                <FiSend size={20} />
              )
            }
          />
        </I.InteractionWrapper>
      </FormProvider>
    </S.Container>
  );
};

export default memo(PublicQuestions);
